import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  ReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  Edge,
  Connection,
  Controls,
  useReactFlow,
  OnConnectStartParams,
  Position,
  EdgeChange,
  applyEdgeChanges
} from '@xyflow/react';
import { useFlowStore } from '../store';
import { FlowNode } from '../types';
import { FlowNodeComponent } from '../FlowNodeComponent';
import { Cascader, Flex, message, Popconfirm, Button } from 'antd';
import type { CascaderProps } from 'antd';
import { NodeData, Edge as GetMapById200ResponseEdgesInner } from "../../api/api"
import AuthenticatedLayout from '../layout/AuthenticatedLayout';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { layoutNodes } from "../../utils/LayoutNodes";

const nodeTypes = {
  flowNode: FlowNodeComponent,
};

interface Option {
  value: string;
  label: string;
}

export const FlowChart: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { topic } = useParams<{ topic: string }>(); 
  const { nodes, edges, postNode, getMap, clearError, error, postEdge, deleteEdge, setLastMap } = useFlowStore();
  const reactFlowWrapper = useRef<HTMLDivElement | null>(null);
  const sourceNodeId = useRef<string | null>(null);
  const fromTarget = useRef<boolean>(null);
  const madeEdge = useRef<boolean>(null);
  const [startNodeId, setStartNodeId] = useState<string | undefined>();
  const [endNodeId, setEndNodeId] = useState<string | undefined>();
  const { fitView } = useReactFlow();
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState(nodes);
  const [rfEdges, setRfEdges] = useEdgesState<Edge>(edges);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { screenToFlowPosition } = useReactFlow();

  const options = nodes.map((obj) => ({ label: obj.data.title, value: obj.id }));

  const [messageApi, contextHolder] = message.useMessage();
  fromTarget.current = false
  madeEdge.current = false

  const isTouchDevice = typeof window !== "undefined" && (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  );

  const errorDisplayer = useCallback((content: string | React.ReactNode) => {
    messageApi.open({
      type: 'error',
      content: content,
      duration: 2.5,
    }).then(() => clearError());
  }, [messageApi, clearError]);

  useEffect(() => {
    if (error != null) errorDisplayer(error);
  }, [error, errorDisplayer]);

  useEffect(() => {
    setLastMap(location.pathname + location.search);
  }, [setLastMap, location]);

  const findPaths = useCallback(
    (
      startNodeId: string,
      endNodeId: string,
      nodes: FlowNode[],
      edges: Edge[]
    ): { pathNodes: FlowNode[]; pathEdges: Edge[] } => {
      const nodeMap = new Map<string, FlowNode>();
      const edgeMap = new Map<string, Edge[]>();
      const pathNodes = new Set<FlowNode>();
      const pathEdges = new Set<Edge>();
    
      // Create lookup maps
      nodes.forEach((node) => nodeMap.set(node.id, node));
      edges.forEach((edge) => {
        if (!edgeMap.has(edge.source)) {
          edgeMap.set(edge.source, []);
        }
        edgeMap.get(edge.source)?.push(edge);
      });
    
      function dfs(currentNodeId: string, visited: Set<string>): boolean {
        if (visited.has(currentNodeId)) return false;
    
        visited.add(currentNodeId);
    
        const currentNode = nodeMap.get(currentNodeId);
        if (!currentNode) return false;
    
        let foundPath = false;
    
        // If the current node is the end node, add it to the path
        if (currentNodeId === endNodeId) {
          pathNodes.add(currentNode);
          visited.delete(currentNodeId); // Backtrack
          return true;
        }
    
        const outgoingEdges = edgeMap.get(currentNodeId) || [];
        for (const edge of outgoingEdges) {
          if (dfs(edge.target, visited)) {
            pathNodes.add(currentNode);
            pathEdges.add(edge);
            foundPath = true;
          }
        }
    
        visited.delete(currentNodeId); // Backtrack
        return foundPath;
      }
    
      dfs(startNodeId, new Set());
    
      return {
        pathNodes: Array.from(pathNodes),
        pathEdges: Array.from(pathEdges),
      };
    },
    []
  );

  const findOneStepConnections = useCallback(
    (
      nodeId: string,
      nodes: FlowNode[],
      edges: Edge[]
    ): { pathNodes: FlowNode[]; pathEdges: Edge[] } => {
      const nodeMap = new Map<string, FlowNode>();
      const pathNodes = new Set<FlowNode>();
      const pathEdges = new Set<Edge>();
    
      // Create a lookup map for nodes
      nodes.forEach((node) => nodeMap.set(node.id, node));
    
      // Add the given node to the result
      const startNode = nodeMap.get(nodeId);
      if (startNode) {
        pathNodes.add(startNode);
      }
    
      // Find edges that have the given node as their source or target
      edges.forEach((edge) => {
        if (edge.source === nodeId || edge.target === nodeId) {
          pathEdges.add(edge);
    
          // Add the connected nodes to the result
          const connectedNode = nodeMap.get(
            edge.source === nodeId ? edge.target : edge.source
          );
          if (connectedNode) {
            pathNodes.add(connectedNode);
          }
        }
      });
    
      return {
        pathNodes: Array.from(pathNodes),
        pathEdges: Array.from(pathEdges),
      };
    },
    []
  );

  const updateFlow = useCallback(
    async (startId: string | undefined | null, endId: string | undefined | null) => {
      let pathNodes: FlowNode[];
      let pathEdges: Edge[];

      if (startId && !endId) {
        ({ pathNodes, pathEdges } = findOneStepConnections(startId, nodes, edges));
      } else if (endId && !startId) {
        ({ pathNodes, pathEdges } = findOneStepConnections(endId, nodes, edges));
      } else if (!startId && !endId) {
        pathNodes = nodes;
        pathEdges = edges;
      } else if (startId === endId) {
        if (startId) {
          ({ pathNodes, pathEdges } = findOneStepConnections(startId, nodes, edges));
        } else {
          pathNodes = nodes;
          pathEdges = edges;
        }
      } else {
        ({ pathNodes, pathEdges } = findPaths(startId!, endId!, nodes, edges));

        if (!pathNodes.length || !pathEdges.length) {
          messageApi.error(
            "No direct connection exists. Showing direct connections for both nodes instead."
          );
          const { pathNodes: startConnectionsNodes, pathEdges: startConnectionsEdges } =
            findOneStepConnections(startId!, nodes, edges);
          const { pathNodes: endConnectionsNodes, pathEdges: endConnectionsEdges } =
            findOneStepConnections(endId!, nodes, edges);

          pathNodes = Array.from(new Set([...startConnectionsNodes, ...endConnectionsNodes]));
          pathEdges = Array.from(new Set([...startConnectionsEdges, ...endConnectionsEdges]));
        }
      }

      // <-- AWAIT layoutNodes here
      const laidOutNodes = await layoutNodes(pathNodes, pathEdges);
      setRfNodes(laidOutNodes);
      setRfEdges(pathEdges);

      setTimeout(() => fitView({ padding: 0.2, duration: 500 }), 100);
    },
    [findPaths, findOneStepConnections, nodes, edges, setRfNodes, setRfEdges, messageApi, fitView]
  );


  useEffect(() => {
    getMap(topic!)
  }, [getMap, topic])

  useEffect(() => {
    // This effect now handles both data loading and URL parameter changes
    if (nodes.length > 0 || edges.length > 0) {
      const urlStart = searchParams.get('start');
      const urlEnd = searchParams.get('end');

      // Update the state for the Cascader components
      setStartNodeId(urlStart || undefined);
      setEndNodeId(urlEnd || undefined);

      // Trigger the flow update function with the URL parameters
      updateFlow(urlStart, urlEnd);
    } else {
      // If data is not yet loaded, clear the flow to show a blank state
      setRfNodes([]);
      setRfEdges([]);
    }
  }, [searchParams, nodes, edges, updateFlow, setRfNodes, setRfEdges]);

  const onStartChange: CascaderProps<Option>['onChange'] = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Check if the value array has a length greater than 0
      if (value && value.length > 0) {
        newParams.set('start', value[0]);
      } else {
        newParams.delete('start');
      }
      return newParams;
    });
  };

  const onEndChange: CascaderProps<Option>['onChange'] = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Check if the value array has a length greater than 0
      if (value && value.length > 0) {
        newParams.set('end', value[0]);
      } else {
        newParams.delete('end');
      }
      return newParams;
    });
  };

  const onNodeClick = (_: React.MouseEvent, node: FlowNode) => {
    const sanitizedTopic = encodeURIComponent(topic as string);
    const url = `/${sanitizedTopic}/wiki-page/${node.id}`;
    navigate(url);
  };

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const tempEdges = [...rfEdges]
      setRfEdges((eds) => applyEdgeChanges(changes, eds));
  
      changes.forEach(async (change) => {
        if (change.type === "remove") {
          const success = await deleteEdge(topic!, change.id);
          if (!success) {
            setRfEdges(tempEdges);
          }
        }
      });
    },
    [setRfEdges, topic, deleteEdge, rfEdges]
  );

  const handleSelectionChange = useCallback(({ edges }: { edges: Edge[] }) => {
  if (edges.length > 0) {
    setSelectedEdgeId(edges[0].id);
  } else {
    setSelectedEdgeId(null);
  }
}, []);

  const onConnectStart = useCallback((_: unknown, { nodeId, handleType }: OnConnectStartParams) => {
    if (handleType === "target") {
      fromTarget.current = true
      return;
    }
    fromTarget.current = false
    sourceNodeId.current = nodeId;
  }, []);

  const onConnect = useCallback(async (params: Edge | Connection) => {
    const { target } = params;
    if (fromTarget.current) {
      return
    }   
    const edge = { id: sourceNodeId.current + "-" + target, source: sourceNodeId.current!, target: target }
    let dataToSubmit = edge as GetMapById200ResponseEdgesInner
    madeEdge.current = true
    const success = await postEdge(topic!, dataToSubmit)
    if (success) {
      setRfEdges((eds) => addEdge(params, eds));
    }
  }, [setRfEdges, topic, postEdge]);
  
  const onConnectEnd = useCallback(
    async (event: MouseEvent | TouchEvent) => {
      if (madeEdge.current) {
        madeEdge.current = false
        return
      }
      if (!sourceNodeId.current) return;
  
      const targetElement = event.target instanceof Element ? event.target : null;
      const targetIsPane = !!targetElement?.closest('.react-flow__pane');
  
      if (targetIsPane && reactFlowWrapper.current) {
        let clientX = 0;
        let clientY = 0;

        if (event instanceof MouseEvent) {
          clientX = event.clientX;
          clientY = event.clientY;
        } else if ('changedTouches' in event && event.changedTouches.length > 0) {
          clientX = event.changedTouches[0].clientX;
          clientY = event.changedTouches[0].clientY;
        } else {
          console.warn("No valid pointer coordinates found");
          return;
        }

        const { success, targetId, sourceId } = await postNode({
          id: sourceNodeId.current,
          topic: topic,
        } as NodeData)

        if (success) {
          const newFlowNode: FlowNode = {
            id: targetId!,
            position: screenToFlowPosition({ x: clientX, y: clientY }),
            type: 'flowNode',
            targetPosition: Position.Left,
            sourcePosition: Position.Right,
            data: { 
              title: `Reload ${targetId}`,
              battleTested: 0,
              fresh: 0,
              speed: 0,
            },
          }
        
          // Merge new node with existing nodes
          const allNodes = [...rfNodes, newFlowNode];

          // Await ELK layout
          const laidOutNodes = await layoutNodes(allNodes, rfEdges);

          // Update state after layout
          setRfNodes(laidOutNodes);

          const edge: Edge = { id: `${sourceId}-${targetId}`, source: sourceId!, target: targetId! };
          setRfEdges((eds) => eds.concat(edge));

          navigate(`/${topic}/wiki-page/${targetId}`);
        };
        return
      }
    },
    [screenToFlowPosition, setRfNodes, setRfEdges, topic, postNode, navigate, rfEdges, rfNodes]
  );

  return (
    <AuthenticatedLayout>
      <div className='flow-root' ref={reactFlowWrapper}>
        <div className='flow-header'>
          <h1 className="center">{topic}</h1>
          {contextHolder}
          <Flex align="center" justify="space-around">
            <Cascader
              options={options}
              onChange={onStartChange}
              value={startNodeId ? [startNodeId] : undefined}
              placeholder="Select Start"
              showSearch={{ filter: (inputValue, path) =>
                path.some((option) =>
                  option.label && (option.label as string).toLowerCase().includes(inputValue.toLowerCase())
                )
              }}
            />
            <Cascader
              options={options}
              onChange={onEndChange}
              value={endNodeId ? [endNodeId] : undefined}
              placeholder="Select End"
              showSearch={{ filter: (inputValue, path) =>
                path.some((option) =>
                  option.label && (option.label as string).toLowerCase().includes(inputValue.toLowerCase())
                )
              }}
            />
          </Flex>
        </div>

        <div className="flow-wrapper">
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onSelectionChange={handleSelectionChange}
            onConnectStart={onConnectStart}
            onNodeClick={onNodeClick}
            onConnectEnd={onConnectEnd}
            nodeTypes={nodeTypes}
          >
            <Controls 
              onFitView={() => fitView({ padding: 0.2, duration: 1000 })}
              showInteractive={false} 
              showZoom={false} 
            />
          </ReactFlow>
        </div>
        
        {isTouchDevice && (
          <div className='sticky-button-container'>
            <Popconfirm
              title="Delete connecting edge?"
              onConfirm={() => {
                if (selectedEdgeId == null) {
                  errorDisplayer("No Edge Selected")
                  return
                }
                handleEdgesChange([ { id: selectedEdgeId, type: 'remove' } ]);
              }}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            >
              <Button danger>
                Delete Edge
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
    </AuthenticatedLayout>

  );
};
